import { ImageContentFit } from "expo-image";

export interface ImageDimensions {
  width: number;
  height: number;
}

const PORTRAIT_THRESHOLD = 0.85;

const LANDSCAPE_THRESHOLD = 1.15;

/**
 * Calculates the aspect ratio from image dimensions
 * @returns aspect ratio (width / height), or null if dimensions are invalid
 */
export const calculateAspectRatio = (
  dimensions: ImageDimensions | null
): number | null => {
  if (!dimensions || dimensions.height === 0 || dimensions.width === 0) {
    return null;
  }
  return dimensions.width / dimensions.height;
};

/**
 * Determines the appropriate resize mode based on aspect ratio
 *
 * Since the gallery container is typically square (1:1), we only use "cover"
 * for images that are close to square (0.85 - 1.15 ratio).
 *
 * - Portrait (ratio < 0.85): "contain" - show full image, prevent top/bottom crop
 * - Near-square (0.85 - 1.15): "cover" - fills container with minimal cropping
 * - Landscape (ratio > 1.15): "contain" - show full image, prevent side crop
 *
 * @returns "contain" or "cover"
 */
export const getResizeModeFromAspectRatio = (
  aspectRatio: number | null,
  fallback: ImageContentFit = "cover",
  _theme?: { isLargeScreen?: boolean }
): ImageContentFit => {
  if (aspectRatio === null) {
    return fallback;
  }

  // Portrait: tall images should not be cropped
  if (aspectRatio < PORTRAIT_THRESHOLD) {
    return "contain";
  }

  // Landscape: wide images should not be cropped
  if (aspectRatio > LANDSCAPE_THRESHOLD) {
    return "contain";
  }

  // Near-square images: fill the container (minimal cropping)
  return "cover";
};

/**
 * Convenience function that calculates aspect ratio and returns resize mode
 */
export const getResizeModeFromDimensions = (
  dimensions: ImageDimensions | null,
  fallback: ImageContentFit = "cover",
  theme?: { isLargeScreen?: boolean }
): ImageContentFit => {
  const aspectRatio = calculateAspectRatio(dimensions);
  return getResizeModeFromAspectRatio(aspectRatio, fallback, theme);
};
